import clsx from 'clsx';
import React from 'react';

import Clipper from '@components/Clipper';
import Comments from '@components/Comments';
import VerticalAdvertisement from '@components/FeaturedPartnerAd/Vertical';
import MostPopularArticles from '@components/MostPopularArticles';
import RelatedStories from '@components/RelatedStories';
import type { MidArticleEventProps } from '@components/pages/Article/MidArticle/Event/interfaces';
import type { Env } from '@contexts/AppContext';
import { useArticleCommentsSummary } from '@hooks/useArticleCommentsSummary';
import useCurrentUser from '@hooks/useCurrentUser';
import useNativeAppVersionCheck from '@hooks/useNativeAppVersionCheck';
import useServerSideRendering from '@hooks/useServerSideRendering';
import type { Article, MostPopularArticle, Pickup, ScriptList } from '@interfaces/homepage';
import { TrackingLocation } from '@tracking';
import { CommentableType } from '@utils/backend-api/comments';
import { getArticleImgSrc } from '@utils/images';

import ArticlePickups from '../ArticlePickups';
import Body from '../Body';
import FeaturedGraphic from '../EmbeddedMedia';
import EndOfArticleDeepResearch from '../EndOfArticle/DeepResearch';
import { type AccessState, MidArticleUnitTypes } from '../interfaces';

interface ContentProps {
  access: AccessState;
  addendum: Article['addendum'];
  advertisement?: Article['advertisement'];
  authors: Article['authors'];
  bodyClassName?: string;
  env: Env;
  freeBlurb: Article['freeBlurb'];
  featuredGraphic?: Article['featuredGraphic'];
  fullText: Article['fullText'];
  highlight?: string;
  id: number;
  midArticleUnitType?: MidArticleUnitTypes;
  mostPopularArticles: MostPopularArticle[];
  newsletterId?: string | number;
  picture?: string;
  pickups?: Pickup[];
  relatedArticles: Article[];
  relatedEvent?: MidArticleEventProps;
  scriptList?: ScriptList[];
  takeaway: Article['takeaway'];
}

const Content = ({
  access,
  addendum,
  advertisement,
  authors,
  bodyClassName,
  env,
  freeBlurb,
  featuredGraphic,
  fullText,
  highlight,
  id,
  midArticleUnitType,
  mostPopularArticles,
  newsletterId,
  picture,
  pickups,
  relatedArticles,
  relatedEvent,
  scriptList,
  takeaway,
}: ContentProps): JSX.Element => {
  const hydrated = useServerSideRendering();
  const { currentUser } = useCurrentUser();
  const { isApp3_0OrGreater, isNativeApp } = useNativeAppVersionCheck();
  const { commentsCount } = useArticleCommentsSummary(id);
  const isLegacyNativeApp = isNativeApp && !isApp3_0OrGreater;
  const canViewFullArticle = access !== 'locked';
  const clipperProps = {
    contentSelector: '.clipperable-content',
    contentImgixUrl: getArticleImgSrc(id, picture, env),
    logoUrl: `${env.IMGIX_URL}/logos/theinformation/the-information-logo-wordmark.svg`,
  };

  return (
    <>
      <div
        className={clsx('flex flex-col md:flex-row md:gap-72 xl:gap-112', {
          'mb-80': !canViewFullArticle,
        })}
      >
        <div className="flex flex-1 flex-col">
          {featuredGraphic && canViewFullArticle && (
            <FeaturedGraphic access={access} featuredGraphic={featuredGraphic} />
          )}
          <div className="md:ml-[60px] xl:ml-[84px]">
            <Body
              access={access}
              addendum={addendum}
              articleId={id}
              authors={authors}
              className={bodyClassName}
              freeBlurb={freeBlurb}
              fullText={fullText}
              highlight={highlight}
              newsletterId={newsletterId}
              relatedArticles={relatedArticles}
              relatedEvent={relatedEvent}
              scriptList={scriptList}
              takeaway={takeaway}
            />
            {canViewFullArticle && isLegacyNativeApp && <EndOfArticleDeepResearch id={id} />}
            {canViewFullArticle && pickups?.length > 0 && <ArticlePickups pickups={pickups} />}
          </div>
        </div>
        <div className="flex-shrink-0 md:w-[195px] lg:w-[260px] print:hidden">
          {mostPopularArticles.length > 0 && canViewFullArticle && (
            <MostPopularArticles
              className="mt-[260px] hidden md:block lg:pr-[30px] xl:mt-[218px]"
              mostPopularArticles={mostPopularArticles}
            />
          )}
          {advertisement && (
            <VerticalAdvertisement
              advertisement={advertisement}
              className="mt-64 hidden md:block"
              trackingLocation={TrackingLocation.SideFeed}
            />
          )}
        </div>
      </div>
      {hydrated && currentUser?.can_view_comments && (
        <div className="mr-0 mt-80 sm:mt-112 md:mr-[267px] lg:mr-[372px] print:hidden">
          <Comments
            commentableId={id}
            commentableType={CommentableType.Article}
            commentsCount={commentsCount}
          />
        </div>
      )}
      {canViewFullArticle && advertisement && (
        <div className="mx-48">
          <VerticalAdvertisement
            advertisement={advertisement}
            className="mt-48 block md:hidden"
            trackingLocation={TrackingLocation.ContentBody}
          />
        </div>
      )}
      {(!midArticleUnitType ||
        (midArticleUnitType === MidArticleUnitTypes.Event && relatedEvent) ||
        midArticleUnitType === MidArticleUnitTypes.DeepResearch) && (
        <RelatedStories articles={relatedArticles} className="my-80 sm:my-112 print:hidden" />
      )}
      {hydrated && <Clipper {...clipperProps} env={env} />}
    </>
  );
};

export default Content;
